import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import BackNavBar from '../../components/BackNavBar';
import BodyText from '../../components/BodyText';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { RedButton } from '../../components/Button';
import { Pages } from '../../App';
import TextInput from '../../components/TextInput';
import Bookfair from '../../interfaces/bookfair-info';
import useUpdateSessionInfo from '../../utils/updateSessionInfo';
import { ConfigContext } from '../../providers/ConfigContext';
import { setTaxErrorData } from '../../DumbleData';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';
import useMakeSale from '../../hooks/useMakeSale';
import PaymentPayload from '../../interfaces/payment-payload';

interface Props {
	pInfo: PaymentInfo;
	bfInfo: Bookfair;
	isTaxExemptChecked: boolean;
	setBookfairInfo: React.Dispatch<React.SetStateAction<Bookfair>>;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	setTransactionId: React.Dispatch<React.SetStateAction<string>>;
	setSaleResponseInfo: React.Dispatch<React.SetStateAction<SaleResponseInfo>>;
}

const PurchaseOrderPaymentContent = ({ pInfo, bfInfo, isTaxExemptChecked, setBookfairInfo, setCurrentPage, setTransactionId, setSaleResponseInfo }: Props) => {
	const [PONumber, setPONumber] = React.useState<string>('');
	const [PONumberError, setPONumberError] = React.useState(false);
	const POBlankError = 'Blank: This field is required.';

	const { apiBase } = useContext(ConfigContext);
	const [status, setStatus] = useState<number>(200);
	const [makeSale] = useMakeSale();
	const [isButtonProcessing, setIsButtonProcessing] = useState(false);
	const { getSessionInfo } = useUpdateSessionInfo();

	const handleChange = (value: string) => {
		if (value.length <= 100) {
			setPONumber(value);
		}
	};

	const handlePOPaymentSubmit = async () => {
		if (PONumber === '') {
			setPONumberError(true);
			return;
		}
		setPONumberError(false);
		setIsButtonProcessing(true);
		const payload: PaymentPayload = {
			total: {
				amount: pInfo.amountDue.toNumber(),
				tax: {
					amount: pInfo.tax.toNumber(),
					rate: bfInfo.taxRate.toNumber(),
					taxableSubtotal: isTaxExemptChecked ? 0 : pInfo.itemTotal.toNumber(),
					status: bfInfo.taxStatus,
					selectedExempt: isTaxExemptChecked,
				},
			},
			tenders: {
				po: {
					amount: pInfo.amountDue.toNumber(),
					id: PONumber,
				},
			},
		};
		const { data, error } = await makeSale(payload);
		if (error) {
			if (error.status === 401) {
				setCurrentPage(Pages.Landing);
			}
			setIsButtonProcessing(false);
			setStatus(error.status);
			console.error(error);
		} else if (data != null) {
			setTransactionId(data.parentTransactionId);
			setSaleResponseInfo(data);
			window._satellite.track('purchase-order-payment-submit');
			setCurrentPage(Pages.Confirmation);
		}
	};

	// If there is a mismatch with tax values, retrieve up-to-date tax info and send to new purchase page
	const handleTaxDiscrepancy = async () => {
		const success = await getSessionInfo(apiBase, setBookfairInfo);
		if (success) {
			setCurrentPage(Pages.NewPurchase);
		} else {
			setCurrentPage(Pages.Landing);
		}
	};

	// Determine error message based on error code
	let errorMessage: React.ReactNode = null;
	if (status === 422) {
		setTaxErrorData(true);
		errorMessage = (
			<>
				{'There appears to be a discrepancy in the sales tax amount. In order to proceed, please '}
				<StyledLink onClick={handleTaxDiscrepancy}>click here</StyledLink>
				{' to recalculate the sales tax.'}
			</>
		);
	} else if (status !== 200) {
		errorMessage = 'We are unable to process your PO. Please use a different payment method or call 877-245-0903.';
	}

	return (
		<>
			<BackNavBar setCurrentPage={setCurrentPage} title="Purchase Order Payment" location={Pages.PaymentMethod} />
			<STFRedeemPage>
				<ContentWrapper>
					<CalculationsDiv>
						<RowItemDiv>
							<ItemLabel>Item Total</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.itemTotal.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Tax</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.tax.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Amount Due</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.amountDue.toNumber())}</ItemValue>
						</RowItemDiv>
						<StyledInputDiv>
							<TextInput label="PO Number" placeholder="" error={PONumberError} errorText={POBlankError} value={PONumber} onChange={handleChange} />
						</StyledInputDiv>
						<StyledRedButton disabled={isButtonProcessing || status !== 200} handleClick={handlePOPaymentSubmit}>
							Submit Payment
						</StyledRedButton>
						{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
					</CalculationsDiv>
				</ContentWrapper>
			</STFRedeemPage>
		</>
	);
};
const STFRedeemPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;
const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 280px;
	margin-top: 53px;
`;
const CalculationsDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 38px;
`;
const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;
const ItemLabel = styled(BodyText)`
	display: flex;
	align-items: center;
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;
const ItemValue = styled(BodyText)`
	display: flex;
	align-items: center;
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;
const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;
const StyledRedButton = styled(RedButton)`
	width: 100%;
	margin: 27px 0 0 0;
`;
const StyledInputDiv = styled.div`
	margin-top: 13px;
	width: 100%;
`;

const ErrorText = styled(BodyText)`
	font-size: 13px;
	margin-top: 10px;
	text-align: left;
`;
const StyledLink = styled.a`
	text-decoration: underline;
	cursor: pointer;
`;

export default PurchaseOrderPaymentContent;
